import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectAgreementsData, selectUser } from '../../store/general/general-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { createAgreementAcceptance, getAgreements } from '../../store/general/general-actions';
import { DateTime } from 'luxon';
import {
  AgreementType,
  ContentType,
  getChangesInstructionsKey,
} from '../../models/enums/agreements';
import UserRole from '../../models/enums/user-roles';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { SelectChangeEvent, Theme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import SelectInput from '../forms/SelectInput';
import i18n, { STORED_LANG_KEY } from '../../i18n';
import { CloseFullScreenIcon, OpenFullScreenIcon, OpenLinkIcon } from '@common/utils/icons/icons';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import { resetAgreementsRestState } from '../../store/general/general-slice';
import ListAgreement from '../../models/general/agreements/list-agreement';
import OrganizationRole from '../../models/enums/organization-roles';
import { Language } from '../../models/enums/languages';
import useSecureFileEffect from '../../hooks/useSecureFileEffect';
import CustomLoader from '@common/components/Feedback/CustomLoader';
import i18next from 'i18next';
import SphereNavigationRoutes from '@common/enums/query-params/sphere-navigation-routes';
import { getLocalizationOrNull } from '@common/i18n';

const useStyles = makeStyles()((theme) => ({
  surveyContent: {
    height: theme.spacing(50),
  },
}));

const AgreementPopups: React.FC<{ demo?: { agreement: ListAgreement; onClose: () => void } }> = ({
  demo,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const dispatch = useAppDispatch();
  const matchesSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const agreementsData = useAppSelector((state) => selectAgreementsData(state));
  const user = useAppSelector((state) => selectUser(state));

  const [language, setLanguage] = useState(user.data.language as string);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [closingSurvey, setClosingSurvey] = useState(false);
  const [surveyCloseButtonDisabled, setSurveyCloseButtonDisabled] = useState(false);
  const [linkLocalization, setLinkLocalization] = useState<string | null>(null);

  useEffect(() => {
    if (agreementsData.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getAgreements());
    }
  }, [agreementsData.rest.getStatus, dispatch]);

  useEffect(() => {
    if (agreementsData.rest.postAcceptAgreementStatus === HttpFetchStatus.Success) {
      dispatch(getAgreements());
    }
  }, [agreementsData.rest.postAcceptAgreementStatus, dispatch]);

  const languageChangeHandler = (event: SelectChangeEvent<unknown>) => {
    const language = event.target.value as string;
    setLanguage(language);
    i18next.changeLanguage(language, (err, t) => {
      if (err) {
        return console.log('language could not be changed', err);
      } else {
        localStorage.setItem(STORED_LANG_KEY, language);
      }
    });
  };

  const toggleFullscreenClickHandler = () => {
    setIsFullScreen((prevState) => !prevState);
  };

  const toggleAgreementCheckedClickHandler = () => {
    setIsAgreementChecked((prevState) => !prevState);
  };

  const resetAgreementPostStatusHandler = () => {
    dispatch(
      resetAgreementsRestState({
        postAcceptAgreementStatus: HttpFetchStatus.Idle,
        postAcceptAgreementError: null,
      }),
    );
  };

  const agreeClickHandler = () => {
    if (demo != null) {
      demo.onClose();
      return;
    }
    if (pendingAgreements != null) {
      if (pendingAgreements[0].contentType === ContentType.SURVEY) {
        setClosingSurvey(true);
      } else {
        setClosingSurvey(false);
      }
      dispatch(
        createAgreementAcceptance({
          accepted:
            pendingAgreements[0].contentType === ContentType.SURVEY ? true : isAgreementChecked,
          agreementId: pendingAgreements[0].id,
          userId: user.data.id,
          organizationId:
            pendingAgreements[0].agreementType === AgreementType.ORGANIZATION
              ? user.data.organization.id
              : null,
        }),
      );
      setIsAgreementChecked(false);
    }
  };

  let pendingAgreements = agreementsData.data
    ?.filter(
      (agreement) =>
        new Date(agreement.validFrom).getTime() <= new Date(DateTime.utc().toISO()).getTime() &&
        agreement.enabled &&
        agreement.agreementType === AgreementType.USER &&
        (agreement.contentType === ContentType.LINK ||
          agreement.contentType === ContentType.SURVEY ||
          agreement.files.length > 0) &&
        !user.data.agreementAcceptances.some(
          (acceptance) => acceptance.agreementId === agreement.id && acceptance.accepted,
        ),
    )
    .sort((a, b) => a.order - b.order);

  if (pendingAgreements == null || pendingAgreements.length === 0) {
    pendingAgreements = agreementsData.data
      ?.filter(
        (agreement) =>
          new Date(agreement.validFrom).getTime() <= new Date(DateTime.utc().toISO()).getTime() &&
          agreement.enabled &&
          (agreement.contentType === ContentType.LINK ||
            agreement.contentType === ContentType.SURVEY ||
            agreement.files.length > 0) &&
          agreement.agreementType === AgreementType.ORGANIZATION &&
          ![UserRole.MODERATOR, UserRole.MEMBER].includes(user.data.role) &&
          user.data.organization.role !== OrganizationRole.LOCATION &&
          !user.data.agreementAcceptances.some(
            (acceptance) => acceptance.agreementId === agreement.id && acceptance.accepted,
          ),
      )
      .sort((a, b) => a.order - b.order);
  }

  if (demo != null) {
    pendingAgreements = [demo.agreement];
  }

  const { fileSrc: pendingAgreementSrc } = useSecureFileEffect(
    (() => {
      if (pendingAgreements == null || pendingAgreements.length === 0) return null;
      if (
        pendingAgreements[0].contentType === ContentType.LINK ||
        pendingAgreements[0].contentType === ContentType.SURVEY
      )
        return null;
      const url =
        pendingAgreements[0].files.find((file) => file.language === language)?.file ??
        pendingAgreements[0].files[0].file;
      return window.location.protocol === 'https:' ? url.replace('http', 'https') : url;
    })(),
  );

  const filteredLanguages = Object.values(Language).filter(
    (l) =>
      l === Language.ENGLISH ||
      pendingAgreements?.[0]?.contentType === ContentType.LINK ||
      pendingAgreements?.[0]?.contentType === ContentType.SURVEY ||
      pendingAgreements?.[0]?.files.some((f) => f.language === l),
  );

  useEffect(() => {
    if (
      pendingAgreements != null &&
      pendingAgreements.length > 0 &&
      (pendingAgreements[0].contentType === ContentType.SURVEY ||
        pendingAgreements[0].contentType === ContentType.LINK) &&
      pendingAgreements[0].link != null
    ) {
      const translatedLink =
        pendingAgreements[0].localizations.find((l) => l.language === language)?.link ??
        pendingAgreements[0].link;
      setLinkLocalization(translatedLink);
    }
  }, [language, pendingAgreements]);

  useEffect(() => {
    if (pendingAgreements?.[0]?.contentType !== ContentType.SURVEY) {
      setSurveyCloseButtonDisabled(false);
      return;
    }

    setSurveyCloseButtonDisabled(true);

    const timerId = setTimeout(() => {
      setSurveyCloseButtonDisabled(false);
    }, 5000);

    return () => clearTimeout(timerId);
  }, [pendingAgreements?.[0]?.id]);

  return (
    <React.Fragment>
      {pendingAgreements != null && pendingAgreements.length > 0 && (
        <Dialog
          open={
            pendingAgreements.length > 0 &&
            !window.location.pathname.includes(SphereNavigationRoutes.Legal)
          }
          fullWidth
          maxWidth="md"
          fullScreen={isFullScreen}
          onClose={demo?.onClose}
        >
          <DialogTitle>
            {demo != null && (
              <CustomAlert color="success" severity="info" header={t('info-label-demo')} />
            )}
            <Stack
              direction={matchesSmDown ? 'column' : 'row'}
              justifyContent={matchesSmDown ? 'start' : 'space-between'}
              alignItems={matchesSmDown ? 'start' : 'center'}
            >
              <Typography variant="h2">{t(pendingAgreements[0].titleKey)}</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box>
                  <Button
                    sx={{ whiteSpace: 'nowrap' }}
                    variant="text"
                    startIcon={
                      isFullScreen ? (
                        <CloseFullScreenIcon data-testid="close-full-screen-button" />
                      ) : (
                        <OpenFullScreenIcon data-testid="open-full-screen-button" />
                      )
                    }
                    onClick={toggleFullscreenClickHandler}
                  >
                    {isFullScreen
                      ? t('info-tooltip-close-full-screen')
                      : t('info-tooltip-open-full-screen')}
                  </Button>
                </Box>
                {filteredLanguages.length > 1 && (
                  <SelectInput
                    value={language}
                    onChange={languageChangeHandler}
                    data-testid="select-language"
                  >
                    {filteredLanguages.map((l) => (
                      <MenuItem key={l} value={l}>
                        {t('language-' + l)}
                      </MenuItem>
                    ))}
                  </SelectInput>
                )}
              </Stack>
            </Stack>
          </DialogTitle>
          <DialogContent
            className={
              pendingAgreements[0].contentType === ContentType.SURVEY ? classes.surveyContent : ''
            }
          >
            <Stack spacing={2} height="100%">
              {(pendingAgreements[0].hasPreviousAcceptance
                ? getLocalizationOrNull(
                    getChangesInstructionsKey(pendingAgreements[0].instructionsKey),
                  )
                : getLocalizationOrNull(pendingAgreements[0].instructionsKey)) && (
                <CustomAlert
                  header={t(
                    pendingAgreements[0].hasPreviousAcceptance
                      ? getChangesInstructionsKey(pendingAgreements[0].instructionsKey)
                      : pendingAgreements[0].instructionsKey,
                  )}
                  severity="info"
                />
              )}
              {pendingAgreements[0].contentType === ContentType.FILE &&
                pendingAgreements[0].files.length > 0 &&
                (pendingAgreementSrc != null ? (
                  <iframe
                    src={pendingAgreementSrc}
                    title={t(pendingAgreements[0].titleKey)}
                    width="100%"
                    style={{ maxWidth: '100%', height: '100%' }}
                  />
                ) : (
                  <CustomLoader />
                ))}
              {pendingAgreements[0].contentType === ContentType.LINK &&
                pendingAgreements[0].link != null && (
                  <Link
                    href={linkLocalization || pendingAgreements[0].link}
                    target="_blank"
                    underline="hover"
                    rel="noreferrer"
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    <OpenLinkIcon />
                    {linkLocalization || pendingAgreements[0].link}
                  </Link>
                )}
              {pendingAgreements[0].contentType === ContentType.SURVEY &&
                pendingAgreements[0].link != null && (
                  <iframe
                    src={
                      (linkLocalization || pendingAgreements[0].link) +
                      `?email=${encodeURIComponent(user.data.email)}`
                    }
                    title={t(pendingAgreements[0].titleKey)}
                    width="100%"
                    style={{ maxWidth: '100%', height: '100%' }}
                  />
                )}
              {pendingAgreements[0].contentType !== ContentType.SURVEY && (
                <FormControlLabel
                  control={<Checkbox />}
                  checked={isAgreementChecked}
                  onChange={toggleAgreementCheckedClickHandler}
                  label={t(pendingAgreements[0].agreementKey)}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={agreeClickHandler}
              variant="contained"
              disabled={
                (pendingAgreements[0].contentType === ContentType.SURVEY &&
                  surveyCloseButtonDisabled) ||
                (pendingAgreements[0].contentType !== ContentType.SURVEY && !isAgreementChecked) ||
                agreementsData.rest.postAcceptAgreementStatus === HttpFetchStatus.Loading
              }
              sx={{ width: 'max-content' }}
            >
              {pendingAgreements[0].contentType === ContentType.SURVEY
                ? t('button-close')
                : t('button-confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <CustomSnackbar
        open={
          agreementsData.rest.postAcceptAgreementStatus === HttpFetchStatus.Success &&
          !closingSurvey
        }
        title={t('success-accepting-agreement')}
        onClose={resetAgreementPostStatusHandler}
      />
    </React.Fragment>
  );
};

export default AgreementPopups;
